import { useCallback, useMemo, useRef } from 'react';
import { ANIMATION_DELAY } from '@ricoh/ria.client.core/constants/ui/animation';
import { useCoreStoreDispatch } from '@ricoh/ria.client.core/plugins/store/hooks/dispatch';
import { componentPopupSliceActions } from '@ricoh/ria.client.core/plugins/store/slices/component/popup';
import { newUUID } from '@ricoh/ria.client.core/utils/data/key';

import type { ToastHook } from './popup.type';

/**
 * Use Toast.
 *
 * @returns {ToastHook} Toast hook.
 */
export function useToast(): ToastHook {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const dispatch = useCoreStoreDispatch();

  const push = useCallback<ToastHook['push']>(
    ({ closeAfter, ...state }) => {
      const key = newUUID(true);

      dispatch(
        componentPopupSliceActions.toast_push({
          key,
          state,
        }),
      );

      timeoutRef.current = setTimeout(
        () => {
          dispatch(componentPopupSliceActions.toast_pop(key));
        },
        closeAfter ??
          Math.min(
            Math.max((state.title.length + state.message.length) * 80, ANIMATION_DELAY.MEDIUM), // Auto calculate 80ms/character
            2 * ANIMATION_DELAY.VERY_SLOW,
          ),
      );
    },
    [dispatch],
  );

  return useMemo<ToastHook>(
    () => ({
      push,
    }),
    [push],
  );
}
